import { useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../api/client';
import { communications, documents, identity, parties, requests } from '../api/endpoints';
import type {
    AutoLinkedItem, ChannelAccount, ClientAccount, ConversationMessage,
    InboxDecision, InboxItem, RequestEntity
} from '../api/types';
import { FilePreviewModal } from '../components/FilePreviewModal';
import {
    Card, Column, DataTable,
    ErrorBox, Field, fmtDate, label, PageHeader, shortId, StatusBadge
} from '../components/ui';
import { navigate, useAsync } from '../hooks';

type DecisionMode = '' | 'new' | 'existing' | 'not_request';

const DECISION_LABELS: Record<string, string> = {
  new: 'Новая заявка',
  existing: 'В существующую',
  not_request: 'Не заявка',
};

// --- Нормализация элемента очереди: backend может вернуть «плоское»
//     сообщение или обёртку { message: {...} } — читаем оба варианта. ---

function innerMessage(it: InboxItem) {
  return it.message && typeof it.message === 'object' ? it.message : null;
}

export function inboxMessageId(it: InboxItem): string {
  if (it.message_id) return it.message_id;
  if (typeof it.message === 'string') return it.message;
  const inner = innerMessage(it);
  if (inner?.id) return inner.id;
  return it.id;
}

function inboxSender(it: InboxItem): string {
  return it.sender ?? innerMessage(it)?.sender ?? it.peer_address ?? '—';
}

function inboxSubject(it: InboxItem): string {
  return it.subject ?? innerMessage(it)?.subject ?? '';
}

function inboxSnippet(it: InboxItem): string {
  const body = it.snippet ?? it.body ?? innerMessage(it)?.body ?? '';
  return body.length > 160 ? `${body.slice(0, 160)}…` : body;
}

function inboxTime(it: InboxItem): string {
  return fmtDate(it.sent_at ?? innerMessage(it)?.sent_at ?? it.received_at ?? it.created_at);
}

function isWhatsAppItem(it: InboxItem): boolean {
  const t = (it.channel_type ?? it.provider
    ?? innerMessage(it)?.channel_type ?? innerMessage(it)?.provider ?? '');
  return ['whatsapp', 'whatsapp_cloud', 'evolution'].includes(String(t))
    || (it.channel_label ?? '').startsWith('WhatsApp');
}

function WaTick({ status }: { status?: string }) {
  if (status === 'failed') return <span className="wa-tick" title="Ошибка доставки">⚠</span>;
  if (status === 'read') return <span className="wa-tick wa-tick-read" title="Прочитано">✓✓</span>;
  if (status === 'delivered') return <span className="wa-tick" title="Доставлено">✓✓</span>;
  if (status === 'sent' || status === 'sent_unconfirmed') return <span className="wa-tick" title="Отправлено">✓</span>;
  return <span className="wa-tick" title="В очереди">…</span>;
}

function inboxAttachCount(it: InboxItem): number {
  if (it.attachments_count != null) return it.attachments_count;
  const own = it.attachments ?? innerMessage(it)?.attachments;
  return own?.length ?? 0;
}

function channelLabel(it: InboxItem, accounts: Map<string, ChannelAccount>): string {
  // Серверная подпись канала (в т.ч. имя пользовательского ящика) —
  // без админ-доступа к справочнику каналов.
  if (it.channel_label) return it.channel_label;
  const acc = it.channel ? accounts.get(it.channel) : undefined;
  if (acc) {
    const type = acc.type === 'whatsapp' ? 'WhatsApp' : acc.type === 'email' ? 'Email' : acc.type ?? 'Канал';
    return acc.mailbox_or_number ? `${type} · ${acc.mailbox_or_number}` : type;
  }
  const t = it.channel_type ?? it.channel_provider ?? innerMessage(it)?.channel_type ?? it.provider ?? innerMessage(it)?.provider;
  if (t === 'whatsapp' || t === 'whatsapp_cloud') return 'WhatsApp';
  if (t === 'email' || t === 'imap' || t === 'webhook_email') return 'Email';
  return t ?? (it.channel ? shortId(it.channel) : '—');
}

// ---------- Панель решения ----------

function DecidePanel({ mode, selectedIds, onDone, onError, initialRequestId }: {
  mode: DecisionMode;
  selectedIds: string[];
  onDone: (notice: string, requestId?: string) => void;
  onError: (e: unknown) => void;
  initialRequestId?: string;
}) {
  const accounts = useAsync<ClientAccount[]>(
    () => (mode === 'new' ? parties.clientAccounts() : Promise.resolve([])),
    [mode],
  );
  const requestList = useAsync<RequestEntity[]>(
    () => (mode === 'existing' ? requests.list() : Promise.resolve([])),
    [mode],
  );
  const [accountId, setAccountId] = useState('');
  const [requestId, setRequestId] = useState(initialRequestId ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  if (!mode) return null;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await communications.decide({
        message_ids: selectedIds,
        decision: mode,
        client_account_id: accountId || undefined,
        request_id: mode === 'existing' ? requestId : undefined,
        reason: reason || undefined,
      });
      const createdId = res?.request_id ?? res?.results?.find((r) => r.request_id)?.request_id;
      const labelText = res?.request_number ? `заявка ${res.request_number}` : 'заявка';
      onDone(
        mode === 'new' ? `Решение применено — создана ${labelText}.`
          : mode === 'existing' ? 'Решение применено — сообщения привязаны к заявке.'
          : 'Решение применено — сообщения помечены как «не заявка».',
        createdId,
      );
    } catch (x) {
      onError(x);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-form" data-testid="inbox-decide-panel">
      {mode === 'new' && (
        <Field label="Клиентский счёт" hint="Если не выбран — backend попробует определить по контакту отправителя">
          <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            <option value="">— определить автоматически —</option>
            {(accounts.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>{a.party_name ?? a.party}</option>
            ))}
          </select>
        </Field>
      )}
      {mode === 'existing' && (
        <Field label="Заявка">
          <select value={requestId} onChange={(e) => setRequestId(e.target.value)} required>
            <option value="">— выберите заявку —</option>
            {(requestList.data ?? []).map((r) => (
              <option key={r.id} value={r.id}>{r.number ?? shortId(r.id)}</option>
            ))}
          </select>
        </Field>
      )}
      {mode === 'not_request' && (
        <Field label="Причина">
          <input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="Например: спам, информационное письмо" />
        </Field>
      )}
      <div className="form-actions">
        <button className="btn primary" onClick={submit}
                disabled={busy || selectedIds.length === 0 || (mode === 'existing' && !requestId)}>
          {busy ? 'Применение…' : `Применить (${selectedIds.length})`}
        </button>
      </div>
    </div>
  );
}

// ---------- Страница ----------

export function InboxPage() {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [mode, setMode] = useState<DecisionMode>('');
  const [actionErr, setActionErr] = useState<unknown>(null);
  const [notice, setNotice] = useState('');
  const [noticeRequestId, setNoticeRequestId] = useState<string | undefined>();
  const [prefillRequestId, setPrefillRequestId] = useState('');
  const [previewIdx, setPreviewIdx] = useState<{ items: { blobId: string; name: string }[]; index: number } | null>(null);
  const [openMsg, setOpenMsg] = useState<InboxItem | null>(null);
  // Локальное состояние «прочитано» — обновляем мгновенно, не дожидаясь reload.
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  // Локальные пометки «важно» до прихода серверного состояния.
  const [flagIds, setFlagIds] = useState<Set<string>>(new Set());
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyAtts, setReplyAtts] = useState<Array<{ id: string; name: string }>>([]);
  const [replyBusy, setReplyBusy] = useState(false);
  const [replyErr, setReplyErr] = useState<unknown>(null);
  const replyFile = useRef<HTMLInputElement | null>(null);
  const [waHistory, setWaHistory] = useState<ConversationMessage[]>([]);
  const [waMore, setWaMore] = useState(false);
  const [waBusy, setWaBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiErr, setAiErr] = useState<unknown>(null);

  const loadHistory = async (convId: string, offset = 0) => {
    setWaBusy(true);
    try {
      const res = await communications.conversationHistory(
        convId, { limit: 5, offset });
      setWaMore(res.has_more);
      setWaHistory((prev) => offset ? [...res.messages, ...prev] : res.messages);
    } catch {
      // История недоступна — показываем только само письмо.
    } finally {
      setWaBusy(false);
    }
  };

  // Фильтр ящиков — мультивыбор; пустой набор = «все ящики».
  // Стартовое значение из sessionStorage (мгновенный рендер), затем
  // серверная UserPreference перекрывает — фильтр общий на все устройства.
  const [channelFilter, setChannelFilterState] = useState<Set<string>>(() => {
    try {
      return new Set((sessionStorage.getItem('inbox_channel_filter') ?? '')
        .split(',').filter(Boolean));
    } catch {
      return new Set();
    }
  });
  const [prefsLoaded, setPrefsLoaded] = useState(false);
  // Пользователь уже кликал чекбоксы — серверная настройка (прилетает
  // асинхронно позже) его выбор не перезатирает.
  const filterTouched = useRef(false);
  useEffect(() => {
    identity.preferences()
      .then((p) => {
        const v = p['inbox_channels'];
        if (Array.isArray(v) && !filterTouched.current) {
          setChannelFilterState(new Set(v.map(String)));
          try {
            sessionStorage.setItem(
              'inbox_channel_filter', v.map(String).join(','));
          } catch { /* private mode */ }
        }
        setPrefsLoaded(true);
      })
      .catch(() => setPrefsLoaded(true));
  }, []);
  const setChannelFilter = (next: Set<string>) => {
    filterTouched.current = true;
    setChannelFilterState(next);
    try {
      sessionStorage.setItem('inbox_channel_filter', [...next].join(','));
    } catch { /* private mode — фильтр не сохранится локально */ }
    if (prefsLoaded) {
      void identity.setPreference('inbox_channels', [...next])
        .catch(() => undefined);
    }
  };
  const channelKey = useMemo(
    () => [...channelFilter].sort().join(','), [channelFilter]);
  // Поиск по полям сообщения — ищет только внутри выбранных каналов
  // (фильтр на бэкенде применяется поверх ?channel=…).
  const [search, setSearch] = useState('');
  const [searchQ, setSearchQ] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(search.trim()), 400);
    return () => clearTimeout(t);
  }, [search]);
  // Первичный запрос ждёт серверную настройку фильтра: иначе очередь
  // грузится «по всем ящикам», а галочки потом показывают восстановленный
  // набор — видимое расхождение. prefsLoaded=true и на ошибке загрузки.
  const list = useAsync<InboxItem[]>(
    () => {
      if (!prefsLoaded) return Promise.resolve([] as InboxItem[]);
      const params: Record<string, unknown> = {};
      if (channelKey) params.channel = channelKey;
      if (searchQ) params.search = searchQ;
      return communications.inbox(params);
    },
    [channelKey, prefsLoaded, searchQ],
  );
  const accounts = useAsync<ChannelAccount[]>(
    () => communications.channelAccounts().catch(() => [] as ChannelAccount[]),
    [],
  );
  // Письма, автоматически связанные с заявками — куда ушло из очереди.
  const linked = useAsync<AutoLinkedItem[]>(
    () => prefsLoaded
      ? communications.inboxAutoLinked(
          channelKey ? { channel: channelKey } : undefined,
        ).catch(() => [] as AutoLinkedItem[])
      : Promise.resolve([] as AutoLinkedItem[]),
    [channelKey, prefsLoaded],
  );
  // История решений доступна не всем ролям — 403 просто скрывает блок.
  const decisions = useAsync<InboxDecision[] | null>(
    () => communications.inboxDecisions().catch((e) => {
      if (e instanceof ApiError && (e.isForbidden || e.isNotFound)) return null;
      throw e;
    }),
    [],
  );

  const accountMap = useMemo(
    () => new Map((accounts.data ?? []).map((a) => [a.id, a])),
    [accounts.data],
  );

  // Автообновление очереди каждые 15 с, пока страница открыта; выбор
  // чекбоксов хранится по id сообщений и при фоновом reload сохраняется.
  useEffect(() => {
    const t = setInterval(() => {
      void list.reload();
      void linked.reload();
    }, 15_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Смена фильтра ящика — снимаем выбор, чтобы решения не уходили
  // по сообщениям, которых уже нет в отфильтрованной очереди.
  useEffect(() => {
    setSelected(new Set());
  }, [channelKey]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const isRead = (r: InboxItem) => !!r.is_read || readIds.has(inboxMessageId(r));
  const isFlagged = (r: InboxItem) => !!r.is_flagged || flagIds.has(inboxMessageId(r));
  const toggleFlag = async (r: InboxItem) => {
    const id = inboxMessageId(r);
    const next = !isFlagged(r);
    setFlagIds((prev) => {
      const s = new Set(prev);
      if (next) s.add(id); else s.delete(id);
      return s;
    });
    try { await communications.flagInbox([id], next); }
    catch { list.reload(); }
  };

  // «Создать заявку (ИИ)»: заявка + привязка атомарно, ИИ заполняет
  // поля фоном — карточка открывается сразу, значения доезжают сами.
  const createAiRequest = async () => {
    if (!openMsg) return;
    setAiBusy(true); setAiErr(null);
    try {
      const res = await communications.inboxCreateRequest(inboxMessageId(openMsg));
      setOpenMsg(null);
      navigate(`/requests/${res.request_id}`);
    } catch (e) {
      setAiErr(e);
    } finally {
      setAiBusy(false);
    }
  };

  const openMessage = (r: InboxItem) => {
    setReplyText(''); setReplyAtts([]); setReplyErr(null); setReplyBusy(false);
    setWaHistory([]); setWaMore(false);
    setAiErr(null); setAiBusy(false);
    setOpenMsg(r);
    if (isWhatsAppItem(r) && r.conversation) {
      void loadHistory(r.conversation);
    }
    const mid = inboxMessageId(r);
    if (!isRead(r)) {
      setReadIds((prev) => new Set(prev).add(mid));
      void communications.markInboxRead([mid], true).catch(() => undefined);
    }
  };

  const setReadState = async (r: InboxItem, read: boolean) => {
    const mid = inboxMessageId(r);
    setReadIds((prev) => {
      const next = new Set(prev);
      if (read) next.add(mid); else next.delete(mid);
      return next;
    });
    try {
      await communications.markInboxRead([mid], read);
    } catch {
      setReadIds((prev) => {
        const next = new Set(prev);
        if (read) next.delete(mid); else next.add(mid);
        return next;
      });
    }
  };

  const sendReply = async () => {
    if (!openMsg || !replyText.trim()) return;
    setReplyBusy(true); setReplyErr(null);
    try {
      await communications.replyInbox(inboxMessageId(openMsg), {
        body: replyText.trim(),
        attachment_ids: replyAtts.map((a) => a.id),
      });
      setNotice('Ответ поставлен в очередь отправки.');
      setOpenMsg(null);
      setReplyText(''); setReplyAtts([]);
      void setReadState(openMsg, true);
    } catch (e) {
      setReplyErr(e);
    } finally {
      setReplyBusy(false);
    }
  };

  const uploadReplyFile = async () => {
    const file = replyFile.current?.files?.[0];
    if (!file) return;
    setReplyBusy(true); setReplyErr(null);
    try {
      const blob = await documents.upload(file, { source_type: 'general' });
      setReplyAtts((prev) => [...prev, { id: blob.id, name: blob.original_name || file.name }]);
      if (replyFile.current) replyFile.current.value = '';
    } catch (e) {
      setReplyErr(e);
    } finally {
      setReplyBusy(false);
    }
  };

  const rows = useMemo(
    () => (list.data ?? []).filter((r) => !unreadOnly || !isRead(r)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list.data, unreadOnly, readIds],
  );
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(inboxMessageId(r)));

  const toggleAll = () => {
    setSelected(allChecked ? new Set() : new Set(rows.map(inboxMessageId)));
  };

  const onDecided = (text: string, requestId?: string) => {
    setNotice(text);
    setNoticeRequestId(requestId);
    setSelected(new Set());
    setMode('');
    list.reload();
    decisions.reload();
  };

  const columns: Column<InboxItem>[] = [
    {
      key: 'sel', title: '', width: '30px',
      render: (r) => (
        <span onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" aria-label="Выбрать сообщение"
                 checked={selected.has(inboxMessageId(r))}
                 onChange={() => toggle(inboxMessageId(r))} />
        </span>
      ),
    },
    {
      key: 'flag', title: '', width: '34px',
      render: (r) => (
        <button className="flag-btn" title={isFlagged(r) ? 'Снять «Важно»' : 'Важно'}
                style={{ background: 'none', border: 'none', cursor: 'pointer',
                         fontSize: 16, color: isFlagged(r) ? '#eab308' : '#cbd5e1',
                         padding: 0 }}
                onClick={(e) => { e.stopPropagation(); void toggleFlag(r); }}>
          {isFlagged(r) ? '★' : '☆'}
        </button>
      ),
    },
    {
      key: 'sender', title: 'Отправитель', width: '170px',
      render: (r) => (
        <span style={{ fontWeight: isRead(r) ? 400 : 700 }}>
          {!isRead(r) && <span className="unread-dot" title="Не прочитано" />}
          {inboxSender(r)}
        </span>
      ),
    },
    {
      key: 'subject', title: 'Тема / текст',
      render: (r) => (
        <div>
          {inboxSubject(r) && (
            <div style={{ fontWeight: isRead(r) ? 400 : 700 }}>{inboxSubject(r)}</div>
          )}
          <div className="muted">{inboxSnippet(r) || '—'}</div>
          {r.routing_note && (
            <div className="alert alert-error" style={{ marginTop: 4, fontSize: 12, padding: '4px 8px' }}>
              {r.routing_note}
            </div>
          )}
          {r.suggested_request_id && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              <button className="btn small" onClick={(e) => {
                e.stopPropagation();
                setSelected(new Set([inboxMessageId(r)]));
                setPrefillRequestId(r.suggested_request_id ?? '');
                setMode('existing');
              }}>
                Привязать к {r.suggested_request_number || 'найденной заявке'} →
              </button>
            </div>
          )}
          {(r.candidate_requests ?? []).length > 0 && (
            <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>
              Заявки отправителя: {(r.candidate_requests ?? []).map((c) => (
                <button key={c.id} className="btn small" style={{ marginRight: 4, marginTop: 2 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(new Set([inboxMessageId(r)]));
                          setPrefillRequestId(c.id);
                          setMode('existing');
                        }}>
                  {c.number || c.title}
                </button>
              ))}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'attachments', title: 'Вложения', width: '90px',
      render: (r) => {
        const atts = (r.attachments ?? [])
          .filter((a) => typeof a === 'object') as Array<{ id: string; file_blob: string; name: string }>;
        if (atts.length > 0) {
          const a = atts[0];
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <button className="btn small" title={`${a.name} — всего вложений: ${atts.length}`}
                      onClick={() => setPreviewIdx({
                        items: atts.map((x) => ({ blobId: x.file_blob, name: x.name })),
                        index: 0,
                      })}>
                📎 {atts.length > 1 ? `×${atts.length}` : (a.name.length > 8 ? `${a.name.slice(0, 8)}…` : a.name)}
              </button>
            </div>
          );
        }
        const n = inboxAttachCount(r);
        return n > 0 ? <span className="badge info">{n}</span> : <span className="muted">—</span>;
      },
    },
    {
      key: 'status', title: 'Статус', width: '90px',
      render: (r) => <StatusBadge value={r.status ?? r.delivery_status ?? 'pending'} />,
    },
    {
      key: 'channel', title: 'Канал', width: '130px',
      render: (r) => channelLabel(r, accountMap),
    },
    { key: 'time', title: 'Время', render: (r) => inboxTime(r), width: '115px' },
  ];

  return (
    <div>
      <PageHeader title="Входящие">
        <button className="btn" onClick={() => { list.reload(); linked.reload(); decisions.reload(); }}>Обновить</button>
      </PageHeader>
      <div className="muted" style={{ marginBottom: 8, fontSize: 13 }}>
        Здесь показаны необработанные сообщения. Письма, связанные с заявками,
        находятся в переписке соответствующих заявок. Список обновляется автоматически.
      </div>

      {notice && (
        <div className="alert alert-info">
          <span>{notice}</span>
          {noticeRequestId && (
            <a href={`#/requests/${noticeRequestId}`}
               onClick={(e) => { e.preventDefault(); navigate(`/requests/${noticeRequestId}`); }}>
              Открыть заявку →
            </a>
          )}
        </div>
      )}
      <ErrorBox error={actionErr} />
      <ErrorBox error={list.error} onRetry={list.reload} />

      {selected.size > 0 && (
        <Card title={`Решение по выбранным (${selected.size})`}>
          <div className="form-actions">
            <button className={`btn small ${mode === 'new' ? 'primary' : ''}`}
                    onClick={() => setMode(mode === 'new' ? '' : 'new')}>
              Новая заявка
            </button>
            <button className={`btn small ${mode === 'existing' ? 'primary' : ''}`}
                    onClick={() => setMode(mode === 'existing' ? '' : 'existing')}>
              В существующую
            </button>
            <button className={`btn small ${mode === 'not_request' ? 'primary' : ''}`}
                    onClick={() => setMode(mode === 'not_request' ? '' : 'not_request')}>
              Не заявка
            </button>
          </div>
          <DecidePanel key={mode + prefillRequestId} mode={mode} selectedIds={[...selected]}
                       initialRequestId={prefillRequestId}
                       onDone={onDecided} onError={setActionErr} />
        </Card>
      )}

      <Card title="Очередь необработанных сообщений"
            actions={(
              <span className="toolbar" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                {(accounts.data ?? []).map((a) => (
                  <label key={a.id} className="muted"
                         style={{ display: 'flex', gap: 4, alignItems: 'center' }}
                         data-testid="inbox-channel-filter">
                    <input type="checkbox"
                           checked={channelFilter.has(a.id)}
                           onChange={() => {
                             const next = new Set(channelFilter);
                             if (next.has(a.id)) next.delete(a.id);
                             else next.add(a.id);
                             setChannelFilter(next);
                           }} />
                    {(a.type === 'whatsapp' ? 'WhatsApp' : a.type === 'email' ? 'Email' : a.type || 'Канал')}
                    {a.mailbox_or_number ? ` · ${a.mailbox_or_number}` : ''}
                  </label>
                ))}
                {channelFilter.size > 0 && (
                  <button className="btn small"
                          onClick={() => setChannelFilter(new Set())}>
                    Все ящики
                  </button>
                )}
                {rows.length > 0 && (
                  <label className="muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input type="checkbox" checked={allChecked} onChange={toggleAll} />
                    Все
                  </label>
                )}
                <label className="muted" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input type="checkbox" checked={unreadOnly}
                         onChange={(e) => setUnreadOnly(e.target.checked)} />
                  Непрочитанные
                </label>
                <input type="search" className="input" placeholder="Поиск по письмам…"
                       style={{ minWidth: 200 }}
                       value={search}
                       onChange={(e) => setSearch(e.target.value)}
                       title="Ищет по отправителю, теме, тексту и адресу — внутри выбранных ящиков" />
              </span>
            )}>
        {list.loading && !list.data ? (
          <div className="loading">Загрузка входящих…</div>
        ) : (
          <DataTable columns={columns} rows={rows}
                     className="inbox-table"
                     onRowClick={openMessage}
                     empty={list.error
                       ? 'Ошибка загрузки — см. сообщение выше'
                       : unreadOnly
                         ? 'Непрочитанных сообщений нет'
                         : 'Очередь пуста — необработанных входящих нет'} />
        )}
      </Card>

      {(linked.data ?? []).length > 0 && (
        <Card title="Направлено в заявки автоматически">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Время</th><th>Канал</th><th>Отправитель</th><th>Тема</th><th>Заявка</th>
                </tr>
              </thead>
              <tbody>
                {(linked.data ?? []).map((m) => (
                  <tr key={m.id}>
                    <td className="nowrap muted">{inboxTime(m)}</td>
                    <td className="muted">{channelLabel(m, accountMap)}</td>
                    <td>{inboxSender(m)}</td>
                    <td>{inboxSubject(m) || '—'}</td>
                    <td>
                      {m.request_id
                        ? <a href={`#/requests/${m.request_id}`}>{m.request_number || shortId(m.request_id)}</a>
                        : '—'}
                      {m.link_reason && (
                        <div className="muted" style={{ fontSize: 12 }}>{m.link_reason}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {decisions.data && decisions.data.length > 0 && (
        <Card title="История решений">
          <ErrorBox error={decisions.error} />
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Время</th><th>Решение</th><th>Сообщение</th><th>Заявка</th><th>Причина</th>
                </tr>
              </thead>
              <tbody>
                {decisions.data.map((d) => (
                  <tr key={d.id}>
                    <td className="nowrap">{fmtDate(d.created_at)}</td>
                    <td>{DECISION_LABELS[d.decision ?? ''] ?? label(d.decision)}</td>
                    <td className="muted">{shortId(d.message ?? d.conversation)}</td>
                    <td>
                      {d.request
                        ? <a href={`#/requests/${d.request}`}>{shortId(d.request)}</a>
                        : '—'}
                    </td>
                    <td>{d.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
      {openMsg && (
        <div className="modal-overlay" onClick={() => setOpenMsg(null)}
             role="dialog" aria-modal="true" data-testid="inbox-message-modal">
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
              <strong style={{ flex: 1 }}>
                {inboxSubject(openMsg) || '(без темы)'}
              </strong>
              <button className="btn small" onClick={() => setOpenMsg(null)}>✕</button>
            </div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 8 }}>
              <div>Канал: {channelLabel(openMsg, accountMap)}</div>
              <div>Отправитель: {inboxSender(openMsg) || '—'}</div>
              <div>Время: {inboxTime(openMsg) || '—'}</div>
              {isRead(openMsg) && (
                <div>Статус: прочитано{openMsg.read_by ? ` (${openMsg.read_by})` : ''}</div>
              )}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <button className="btn small"
                        onClick={() => void toggleFlag(openMsg)}
                        style={{ color: isFlagged(openMsg) ? '#eab308' : undefined }}>
                  {isFlagged(openMsg) ? '★ Важно' : '☆ Отметить важным'}
                </button>
                {isFlagged(openMsg) && openMsg.flagged_by && (
                  <span>({openMsg.flagged_by})</span>)}
              </div>
            </div>
            {isWhatsAppItem(openMsg) ? (
              <div className="wa-chat">
                {waMore && (
                  <button className="btn small" style={{ alignSelf: 'center', margin: '4px 0' }}
                          disabled={waBusy}
                          onClick={() => openMsg.conversation &&
                            void loadHistory(openMsg.conversation, waHistory.length)}>
                    {waBusy ? 'Загрузка…' : 'Загрузить больше'}
                  </button>
                )}
                {waBusy && waHistory.length === 0 && (
                  <div className="muted" style={{ textAlign: 'center', fontSize: 12 }}>
                    Загрузка истории…
                  </div>
                )}
                {waHistory.map((m) => (
                  <div key={m.id}
                       className={`wa-bubble ${m.direction === 'outbound' ? 'wa-out' : 'wa-in'}`}>
                    {m.direction === 'inbound' && m.sender && (
                      <div className="wa-sender">{m.sender}</div>
                    )}
                    <div>{m.body || '(пусто)'}</div>
                    {(m.attachments ?? []).length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        {(m.attachments ?? []).map((a, i) => (
                          <button key={a.id} className="btn small" title={a.name}
                                  style={{ marginRight: 4, marginTop: 2 }}
                                  onClick={() => setPreviewIdx({
                                    items: (m.attachments ?? []).map((x) => ({
                                      blobId: x.file_blob, name: x.name })),
                                    index: i,
                                  })}>
                            📎 {a.name.length > 20 ? `${a.name.slice(0, 20)}…` : a.name}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="wa-meta">
                      {fmtDate(m.sent_at)}
                      {m.direction === 'outbound' && <WaTick status={m.delivery_status} />}
                    </div>
                  </div>
                ))}
                {waHistory.length === 0 && !waBusy && (
                  <div className="wa-bubble wa-in">
                    <div>{openMsg.body || inboxSnippet(openMsg) || '(пусто)'}</div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ whiteSpace: 'pre-wrap', borderTop: '1px solid #e2e5ea',
                            paddingTop: 10, fontSize: 14 }}>
                {openMsg.body || inboxSnippet(openMsg) || '(пусто)'}
              </div>
            )}
            {((openMsg.attachments ?? []).filter(
                (a) => typeof a === 'object') as Array<{ id: string; file_blob: string; name: string }>).length > 0 && (
              <div style={{ marginTop: 10 }}>
                <strong style={{ fontSize: 13 }}>Вложения</strong>
                <div style={{ marginTop: 4 }}>
                  {((openMsg.attachments ?? []).filter(
                      (a) => typeof a === 'object') as Array<{ id: string; file_blob: string; name: string }>)
                    .map((a, i, atts) => (
                      <button key={a.id} className="btn small"
                              style={{ marginRight: 6, marginTop: 4 }} title={a.name}
                              onClick={() => setPreviewIdx({
                                items: atts.map((x) => ({ blobId: x.file_blob, name: x.name })),
                                index: i,
                              })}>
                        📎 {a.name.length > 30 ? `${a.name.slice(0, 30)}…` : a.name}
                      </button>
                    ))}
                </div>
              </div>
            )}
            <div className="toolbar" style={{ marginTop: 14 }}>
              <button className="btn primary" disabled={aiBusy}
                      title="Заявка создаётся из письма, ИИ заполняет поля"
                      onClick={() => void createAiRequest()}>
                {aiBusy ? 'Создаю заявку…' : 'Создать заявку (ИИ)'}
              </button>
              <button className="btn primary" onClick={() => {
                setSelected(new Set([inboxMessageId(openMsg)]));
                setOpenMsg(null);
              }}>
                Выбрать для решения
              </button>
              <button className="btn" onClick={() => void setReadState(openMsg, false)}>
                В непрочитанные
              </button>
              <button className="btn" onClick={() => setOpenMsg(null)}>Закрыть</button>
            </div>
            <ErrorBox error={aiErr} />
            <div style={{ borderTop: '1px solid #e2e5ea', marginTop: 12, paddingTop: 10 }}>
              <strong style={{ fontSize: 13 }}>Ответить</strong>
              <ErrorBox error={replyErr} />
              <textarea className="textarea" rows={3} style={{ width: '100%', marginTop: 6 }}
                        placeholder="Текст ответа (уйдёт тем же каналом — email/WhatsApp)"
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)} />
              {replyAtts.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  {replyAtts.map((a) => (
                    <span key={a.id} className="badge info" style={{ marginRight: 6 }}>
                      {a.name}
                      <button className="btn small" style={{ marginLeft: 4 }}
                              onClick={() => setReplyAtts((p) => p.filter((x) => x.id !== a.id))}>✕</button>
                    </span>
                  ))}
                </div>
              )}
              <div className="toolbar" style={{ marginTop: 6 }}>
                <input ref={replyFile} type="file" style={{ fontSize: 12 }}
                       onChange={() => void uploadReplyFile()} />
                <button className="btn primary" disabled={replyBusy || !replyText.trim()}
                        onClick={() => void sendReply()}>
                  {replyBusy ? 'Отправка…' : 'Отправить ответ'}
                </button>
              </div>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Ответ уходит в тот же диалог без привязки к заявке: email — цепочкой
                (Re: + References), WhatsApp — в чат собеседника.
              </div>
            </div>
          </div>
        </div>
      )}
      {previewIdx && (
        <FilePreviewModal items={previewIdx.items} index={previewIdx.index}
                          onClose={() => setPreviewIdx(null)}
                          onNavigate={(i) => setPreviewIdx((p) => p && { ...p, index: i })} />
      )}
    </div>
  );
}
